/**
 * The words over the valley: the balance, the journal, the goals.
 *
 * Ported from the wall display -- `renderWorld`, `renderJournal`,
 * `renderGoals` and `renderGoalTotal` in apps/display/public/scene.js, and
 * the HUD rules still in force at the end of its scene.css (the block
 * imported from the "Our Valley Display" design, plus what overrides it).
 *
 * WRITTEN FOR ACROSS A ROOM, which is why it differs from the rest of the app:
 *
 *   - Whole dollars. "$40", not "$40.00". The display rounds, and on a
 *     kitchen wall two extra digits are two more things to squint at. The
 *     exact figure is one tap away on a parent's own screen.
 *   - The balance is sized to fit its own width: `--amount-width` grows with
 *     the number of characters, so $5 is enormous and $12,345 still fits.
 *   - A true minus sign (U+2212) on spending, not a hyphen, because at this
 *     size a hyphen is visibly too short and reads as a dash.
 *
 * NOT YET PORTED, and said so rather than faked: the display shows each
 * person's painted FACE beside journal rows and above goals. Those come from
 * its avatar system, which is its own port. The rows and cards here keep the
 * space tidy without them.
 *
 * These take plain props rather than a `FamilyState`, so the preview page can
 * draw them from made-up numbers and a test can hold them to the display's
 * rules without a family.
 */

import { useDragScroll } from '../components/useDragScroll';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Character } from '../avatar/Character';
import { currentUnit, shortAmount } from '../family/money';
import { GoalPicture } from '../brand/GoalPicture';
import { MarbleJar } from './MarbleJar';
import { localToday } from '../family/goals';
import { isShowing, type Goal } from '../family/state';
import type { AvatarSpec } from '../avatar/avatar';

/** Everyone's character, by person id. Somebody without one has no face. */
export type Faces = Readonly<Record<string, AvatarSpec | null>>;

/** The display's cap on faces per row; the rest become "+2". */
export const MAX_FACES = 5;

/**
 * `faceStack` in scene.js. Heads overlap rather than wrap -- the more of
 * them, the further each slides over the last -- so four children read as
 * one clutch from across a room and the words beside them never lose a line.
 */
export function FaceStack({ ids, faces }: { ids: readonly string[]; faces?: Faces }) {
  if (!faces) return null;
  const chosen = ids.filter((id) => faces[id]);
  if (chosen.length === 0) return null;
  const shown = chosen.slice(0, MAX_FACES);
  const over = chosen.length - shown.length;
  const overlap = shown.length < 2 ? 0 : shown.length < 4 ? 0.28 : shown.length < 5 ? 0.44 : 0.56;
  return (
    <span className="face-stack" style={{ ['--overlap' as string]: String(overlap) }} data-count={shown.length}>
      {shown.map((id) => (
        <Character key={id} spec={faces[id]} />
      ))}
      {over ? <span className="face-more" aria-label={`and ${over} more`}>+{over}</span> : null}
    </span>
  );
}

/**
 * `short` in scene.js: whole dollars, the way the wall shows money -- or,
 * since families can count in other units, the short form of theirs ("33"
 * beside a jar of marbles, "⭐ 33").
 */
export function wholeDollars(cents: number): string {
  return shortAmount(cents);
}

// ---------------------------------------------------------------------------

/**
 * The balance, and a brief "+$5" beneath it when it changes.
 *
 * The change is the whole reason a family looks up at the screen: somebody
 * just did a chore and wants to see it land. So it is announced, then fades.
 * Not on first paint -- opening the valley is not a change.
 */
export function BalanceHud({ cents, name }: { cents: number; name?: string | null }) {
  const text = wholeDollars(cents);
  const previous = useRef<number | null>(null);
  const [change, setChange] = useState<{ delta: number; key: number } | null>(null);

  useEffect(() => {
    if (previous.current !== null && previous.current !== cents) {
      setChange({ delta: cents - previous.current, key: Date.now() });
    }
    previous.current = cents;
  }, [cents]);

  return (
    <section
      className="valley-balance-hud"
      aria-label="Current balance"
      style={{
        ['--amount-width' as string]: String(Math.max(3, text.length * 0.59)),
        // Its true width in ems, unclamped: a phone fits the figure between the
        // moon and the builder button with it (living-valley.css 27).
        ['--amount-chars' as string]: String(text.length * 0.6),
      }}
    >
      <div className="valley-balance-stack">
        <div className="hud-frost" aria-hidden="true" />
        {/* Whose valley this is, inside the panel so the two centre as one. */}
        {name ? <p className="valley-balance-name">{name}</p> : null}
        <div className="valley-balance-row">
          {/* Marbles: the jar beside the number, and a chore landing is a
              handful of marbles dropping in while the whole family watches. */}
          {currentUnit().type === 'marbles' ? (
            <span className="valley-balance-jar">
              <MarbleJar count={Math.max(0, Math.floor(cents / 100))} />
            </span>
          ) : null}
          <output className="valley-balance" aria-live="polite">{text}</output>
        </div>
        {change ? (
          <div key={change.key} className="valley-balance-change show" aria-hidden="true">
            {change.delta > 0 ? '+' : '−'}
            {wholeDollars(Math.abs(change.delta))}
          </div>
        ) : (
          <div className="valley-balance-change" aria-hidden="true" />
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------

export interface JournalRow {
  id: string;
  reason: string;
  amount: number;
  voided: boolean;
  /** Who it was for. Their faces go beside it. */
  people?: readonly string[];
}

/** The display's cap. Twenty is a long scroll already at this type size. */
export const JOURNAL_ROWS = 20;

export function journalRows(rows: readonly JournalRow[]): JournalRow[] {
  // Corrections are left out, as the display leaves them out. On a parent's
  // own screen a struck-through row is information; on the kitchen wall it
  // is a child asking why their money was taken away.
  return rows.filter((row) => !row.voided).slice(0, JOURNAL_ROWS);
}

/**
 * The recent entries, scrolling up forever.
 *
 * TWO COPIES of the list, one under the other, animated up by exactly half
 * the track: when the first copy has scrolled out, the second is exactly where
 * the first began, and the loop has no seam. A short list is repeated first,
 * so there are always enough rows to fill the window.
 *
 * The speed is set from the MEASURED height, not the row count -- rows wrap to
 * two lines on a phone -- so it reads at the same pace everywhere.
 */
export function JournalHud({ rows, faces }: { rows: readonly JournalRow[]; faces?: Faces }) {
  const shown = journalRows(rows);
  const viewport = useRef<HTMLDivElement>(null);
  const first = useRef<HTMLDivElement>(null);
  useDragScroll(viewport);

  // SWIPEABLE (2026-10-13). The ticker used to be a CSS animation nobody could
  // touch. It is a scroll area now: on a wide screen it still drifts along at
  // about sixty pixels a second, and stops for a few seconds whenever somebody
  // swipes or drags it, so an entry that went by can be pulled back. Two
  // copies of the list, as before, so the drift wraps without a seam. A phone
  // held upright does not drift: it swipes one entry at a time.
  useEffect(() => {
    const el = viewport.current;
    const copy = first.current;
    if (!el || !copy || shown.length === 0) return;
    const wide = window.matchMedia?.('(min-aspect-ratio: 1/1)');
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    let frame = 0;
    let last = 0;
    let resume = 0;
    let carried = 0;
    const hold = () => {
      resume = performance.now() + 4000;
    };
    const tick = (now: number) => {
      frame = requestAnimationFrame(tick);
      const dt = last ? Math.min(0.1, (now - last) / 1000) : 0;
      last = now;
      const width = copy.offsetWidth;
      if (!width) return;
      // Wrap either way, so a swipe back past the start keeps going too.
      if (el.scrollLeft >= width) el.scrollLeft -= width;
      else if (el.scrollLeft <= 0 && now < resume) el.scrollLeft += width;
      if (!wide?.matches || still?.matches || now < resume || document.hidden) return;
      // scrollLeft only takes whole pixels on some screens: carry the rest.
      carried += 60 * dt;
      const step = Math.floor(carried);
      if (step > 0) {
        el.scrollLeft += step;
        carried -= step;
      }
    };
    frame = requestAnimationFrame(tick);
    el.addEventListener('pointerdown', hold);
    el.addEventListener('wheel', hold, { passive: true });
    el.addEventListener('touchstart', hold, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      el.removeEventListener('pointerdown', hold);
      el.removeEventListener('wheel', hold);
      el.removeEventListener('touchstart', hold);
    };
  }, [shown.length]);

  if (shown.length === 0) return null;

  const repeat = Math.max(1, Math.ceil(4 / shown.length));
  const content = (copy: number) =>
    Array.from({ length: repeat }, (_, pass) =>
      shown.map((row) => (
        <div
          key={`${pass}-${row.id}`}
          className={[row.amount < 0 ? 'transaction debit' : 'transaction', pass > 0 ? 'repeat' : ''].join(' ').trim()}
          {...(copy > 0 || pass > 0 ? { 'aria-hidden': true } : {})}
        >
          <FaceStack ids={row.people ?? []} {...(faces ? { faces } : {})} />
          <span className="reason">{row.reason || 'A grown-up added this'}</span>
          <span className="money">
            {row.amount < 0 ? '−' : '+'}
            {wholeDollars(Math.abs(row.amount))}
          </span>
        </div>
      )),
    );

  return (
    <section className="valley-journal-hud" aria-label="Recent entries">
      <div className="hud-frost" aria-hidden="true" />
      <span className="journal-tab" aria-hidden="true">
        <span className="journal-tab-wide">Journal</span>
        <span className="journal-tab-narrow">Latest</span>
      </span>
      <div
        ref={viewport}
        className="transaction-viewport swipeable"
        tabIndex={0}
        role="region"
        aria-label="Recent entries, swipe for more"
      >
        <div className="transaction-track">
          <div ref={first}>{content(0)}</div>
          <div className="transaction-copy" aria-hidden="true">{content(1)}</div>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------

export interface GoalCard {
  id: string;
  /** The goal's picture (Living Valley goal art). */
  icon?: string;
  /** Who is saving for it. Their faces sit above the title. */
  people?: readonly string[];
  title: string;
  target: number;
  /** What counts toward it, worked out by the server. See `goal_pot`. */
  pot: number;
  due: string | null;
  status: string;
  /** How long a completed goal stays up: 'always', a date, or null. */
  showUntil?: string | null;
}

export interface GoalView {
  id: string;
  icon?: string;
  people: readonly string[];
  title: string;
  /** Capped at the target and floored at nothing, as the bar shows it. */
  amount: number;
  target: number;
  percent: number;
  reached: boolean;
  /** Completed, and left up by a parent to celebrate. */
  done?: boolean;
  /** "Ready! Ask a grown-up", the due date, or nothing. */
  note: string;
}

/** `renderGoals`, as data. */
export function goalViews(goals: readonly GoalCard[], today = localToday()): GoalView[] {
  return goals
    .filter((goal) => goal.status === 'active' || isShowing({ status: goal.status as Goal['status'], showUntil: goal.showUntil ?? null }, today))
    .map((goal) => {
      if (goal.status === 'completed') {
        // Done, and left up by a parent to celebrate: a full bar and a cheer.
        return {
          id: goal.id,
          ...(goal.icon ? { icon: goal.icon } : {}),
          people: goal.people ?? [],
          title: goal.title,
          amount: goal.target,
          target: goal.target,
          percent: 100,
          reached: true,
          done: true,
          note: 'Done! 🎉',
        };
      }
      const amount = Math.min(goal.target, Math.max(0, goal.pot));
      const reached = goal.pot >= goal.target;
      return {
        id: goal.id,
        ...(goal.icon ? { icon: goal.icon } : {}),
        people: goal.people ?? [],
        title: goal.title,
        amount,
        target: goal.target,
        percent: goal.target > 0 ? Math.floor((amount / goal.target) * 100) : 0,
        reached,
        // Paid for, and waiting on a grown-up to actually buy the thing.
        note: reached ? 'Ready! Ask a grown-up' : goal.due ? dueDate(goal.due) : '',
      };
    });
}

function dueDate(iso: string): string {
  // Noon, so a date never slips a day either side of a timezone boundary.
  return new Date(`${iso}T12:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * All the goals as one bar: what is saved toward them against what they need.
 *
 * The sum of the cards, each capped at its own target. It used to measure the
 * whole family balance against the targets ("could we pay for everything?"),
 * which read as "$70 of $70" while every card underneath was nearly empty.
 * Changed 2026-10-13 on the wall display too (`renderGoalTotal`).
 */
export function goalTotal(goals: readonly GoalView[]) {
  const target = goals.reduce((sum, goal) => sum + goal.target, 0);
  const saved = goals.reduce((sum, goal) => sum + goal.amount, 0);
  return {
    label: goals.length === 1 ? 'Goal total' : `All ${goals.length} goals`,
    saved,
    target,
    percent: target ? Math.floor((saved / target) * 100) : 0,
  };
}

export function GoalHud({
  goals,
  faces,
}: {
  goals: readonly GoalCard[];
  /** No longer used: the total adds up the goals themselves. */
  balance?: number;
  faces?: Faces;
}) {
  const views = goalViews(goals);
  const track = useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = useState(false);
  useDragScroll(track);

  useLayoutEffect(() => {
    const el = track.current;
    if (!el) return;
    const check = () => setOverflow(el.scrollWidth - el.clientWidth > 4);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [views.length]);

  if (views.length === 0) return null;
  // What is still being saved for: a finished goal left up is a celebration,
  // not something the family's money has to cover.
  const total = goalTotal(views.filter((view) => !view.done));

  /** One card along, wrapping round at either end. `scrollGoals` in scene.js. */
  function scroll(direction: 1 | -1): void {
    const el = track.current;
    const card = el?.firstElementChild;
    if (!el || !card) return;
    const step = card.getBoundingClientRect().width + parseFloat(getComputedStyle(el).columnGap || '0');
    const end = el.scrollWidth - el.clientWidth;
    let next = el.scrollLeft + step * direction;
    if (next > end - 2) next = direction > 0 ? 0 : end;
    if (next < 0) next = end;
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    el.scrollTo({ left: next, behavior: still ? 'auto' : 'smooth' });
  }

  return (
    <section className="valley-goal-hud" aria-label="Goals">
      <div className="hud-frost" aria-hidden="true" />
      <div className="goal-row">
        {overflow ? (
          <button type="button" className="goal-nav" aria-label="Previous goal" onClick={() => scroll(-1)}>
            &lsaquo;
          </button>
        ) : null}
        <div
          ref={track}
          className={overflow ? 'goals-track scrollable' : 'goals-track'}
          // Scrolls sideways when there are more goals than fit: reachable by
          // keyboard then, and named, so arrow keys can move along it.
          {...(overflow ? { tabIndex: 0, role: 'region', 'aria-label': 'Goals, scroll for more' } : {})}
        >
          {views.map((goal) => (
            <article
              key={goal.id}
              className={[goal.reached ? 'world-goal reached' : 'world-goal', goal.done ? 'done' : ''].join(' ').trim()}
            >
              {/* Faces on the card's corner, where a child looks first. */}
              <div className="goal-faces">
                <FaceStack ids={goal.people} {...(faces ? { faces } : {})} />
              </div>
              <div className="world-goal-head">
                <GoalPicture icon={goal.icon ?? 'star'} size={40} />
                <h2>{goal.title || 'A surprise'}</h2>
              </div>
              <div className="goal-values">
                {wholeDollars(goal.amount)} / {wholeDollars(goal.target)}
              </div>
              <div
                className="goal-line"
                role="progressbar"
                aria-label={goal.title || 'A goal'}
                aria-valuenow={goal.percent}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <span style={{ width: `${goal.percent}%` }} />
              </div>
              <div className="goal-date">{goal.note}</div>
            </article>
          ))}
        </div>
        {overflow ? (
          <button type="button" className="goal-nav" aria-label="Next goal" onClick={() => scroll(1)}>
            &rsaquo;
          </button>
        ) : null}
      </div>
      <div className="goal-total">
        <span className="goal-total-label">{total.label}</span>
        <span className="goal-total-values">
          {wholeDollars(total.saved)} <span>of</span> {wholeDollars(total.target)}
        </span>
        <span
          className="goal-total-line"
          role="progressbar"
          aria-label="All goals together"
          aria-valuenow={total.percent}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <span style={{ width: `${total.percent}%` }} />
        </span>
      </div>
    </section>
  );
}
